import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Hudiksvall?", a: "Ansökan görs till tillståndsenheten i Hudiksvalls kommun. Verksamhet i annan kommun i Hälsingland (t.ex. Söderhamn, Nordanstig, Ljusdal, Bollnäs) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Hudiksvall?", a: "Hudiksvalls kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Hudiksvall?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-hudiksvall")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Hudiksvall" },
      ]}
      title="Serveringstillstånd Hudiksvall – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Hudiksvall: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.hudiksvall, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Hudiksvall?", body: "Tillståndsenheten i Hudiksvalls kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Söderhamn, Nordanstig, Ljusdal eller Bollnäs ansöker du hos respektive kommun." },
        { h2: "Krav från Hudiksvalls kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Hudiksvall en tydlig sommarsäsong med skärgårds- och Hälsingegårdsturism. Uteserveringar och tillfälliga tillstånd är vanliga sommartid. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Hudiksvall", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför sommarsäsongen." },
        { h2: "Tillsyn och uppföljning", body: "Hudiksvalls kommun bedriver tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Glada Hudik och Hälsingegårdar", body: "Hudiksvall präglas av kustnära turism, fiskelägen och Hälsingegårdar. Många serveringar drivs i äldre kulturhistorisk miljö – kontrollera brand- och lokalkraven tidigt i processen." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-gavle", label: "Serveringstillstånd Gävle" },
          { to: "/serveringstillstand-sundsvall", label: "Serveringstillstånd Sundsvall" },
          { to: "/serveringstillstand-sandviken", label: "Serveringstillstånd Sandviken" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Hudiksvall – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Hudiksvall: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Hudiksvall – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Hudiksvall." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-hudiksvall" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-hudiksvall" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Hudiksvall", url: "https://alkoholprovet.se/serveringstillstand-hudiksvall" },
      ]),
    ],
  }),
});
